import { Divider } from "components/design/Divider";
import { Button } from "components/DesignSystem/Button/Button";
import Modal from "components/DesignSystem/Modal/Modal";
import { StatusModal } from "components/DesignSystem/StatusModal/StatusModal";
import { FINANCIAL_RECORD_TYPE } from "constants/vendorBills";
import { useConstructInternalLink } from "hooks/useConstructInternalLink";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useModal } from "hooks/useModal";
import { useToast } from "hooks/useToast";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useAddBillOrRequestMutation } from "store/apis/vendorBills";
import { closePrepaidExpenseModal } from "store/slices/prepaidExpenseModal";
import { RootState } from "store/store";
import { BackendError } from "types/utils/error";

export const PrepaidExpenseModal = () => {
  const dispatch = useDispatch();
  const { alertToast } = useToast();
  const { uuid: groupId } = useCurrentGroupContext();
  const entityId = useCurrentEntityId();
  const { link } = useConstructInternalLink();
  const linkBillModal = useModal();
  const [linkTransactionId, setLinkTransactionId] = useState<string | null>(
    null
  );

  const { isOpen, transactionId } = useSelector(
    (state: RootState) => state.prepaidExpenseModal
  );

  const [addBillOrRequest, { isLoading: isCreatingBill }] =
    useAddBillOrRequestMutation();

  const onClose = () => {
    dispatch(closePrepaidExpenseModal());
  };

  const handleCreateBillAndLink = async () => {
    try {
      const { uuid } = await addBillOrRequest({
        bill_type: FINANCIAL_RECORD_TYPE.BILL,
        entityId,
        groupId,
      }).unwrap();

      dispatch(closePrepaidExpenseModal());
      window.open(link(`/books/bills-and-vendors/bill/${uuid}`), "_blank");
    } catch (error) {
      alertToast(
        {
          message: (error as BackendError).data?.error?.message,
        },
        error as Error
      );
    }
  };

  const handleLinkToExistingBill = () => {
    if (transactionId) {
      setLinkTransactionId(transactionId);
    }
    dispatch(closePrepaidExpenseModal());
    linkBillModal.open();
  };

  const closeLinkBillModal = () => {
    linkBillModal.close();
    setLinkTransactionId(null);
  };

  if (!isOpen && !linkBillModal.isOpen) {
    return null;
  }

  return (
    <>
      <Modal.Root open={isOpen} onOpenChange={onClose}>
        <Modal.Content size="small">
          <StatusModal
            type="success"
            title="Transaction is categorised as Prepaid expense"
          >
            <div className="t-text-body-sm t-space-y-3">
              <Divider classNames="t-mt-4" type="faded_solid" color="grey">
                <span className="t-text-body-sm-lite t-text-neutral-30 t-italic t-whitespace-nowrap">
                  Suggested next steps
                </span>
              </Divider>
              <div className="t-text-center t-text-text-30 t-text-body-sm">
                Prepaid expenses should be linked to a bill so the cost is
                accrued over its service period.
              </div>
            </div>
          </StatusModal>
          <Modal.Footer>
            <div className="t-flex t-flex-col t-w-full t-gap-2">
              <Button
                block
                onClick={handleCreateBillAndLink}
                isLoading={isCreatingBill}
                disabled={isCreatingBill}
              >
                Create bill
              </Button>
            </div>
          </Modal.Footer>
        </Modal.Content>
      </Modal.Root>
    </>
  );
};
